// Requests/Cancel_Slot.ts
import { getAccessToken } from "../Services/authToken";

export interface CancelSlotResponse {
  success: boolean;
  message: string;
  scheduled_date?: string;
  scheduled_start_time?: string;
  scheduled_end_time?: string;
  interviewer_name?: string;
  requisition_name?: string;
}

const hostname = window.location.hostname;

export async function CancelInterviewSlot(
  candidateEmail: string,
  slotId: string,
  reason: string
): Promise<CancelSlotResponse> {
  let baseurl = "";
  if (hostname.includes("localhost")) {
    baseurl = "http://localhost:8080";
  } else if (hostname.includes("dev")) {
    baseurl = "https://candidateinterviewscheduler-dev.pwc.com";
  } else if (hostname.includes("stg") || hostname.includes("stage")) {
    baseurl = "https://candidateinterviewscheduler-stg.pwc.com";
  } else {
    baseurl = "https://candidateinterviewscheduler.pwc.com"; // fallback production
  }

  // GET TOKEN
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Authentication required - please log in");
  }

  console.log(candidateEmail, slotId, "inside cancel slot");

  const res = await fetch(`${baseurl}/api/candidate-dashboard/cancel-slot`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    // Align with reschedule API contract
    body: JSON.stringify({ slotId, cancellation_reason: reason }),
  });

  if (!res.ok) {
    const errorBody = await res.text();
    console.error("[CancelInterviewSlot] Error response:", errorBody);
    throw new Error(`Cancel failed: ${res.statusText} - ${errorBody}`);
  }

  return res.json();
}

// NEW
// NEW
export async function CancelInterviewSlotExp(
  candidateEmail: string,
  slotId: string,
  reason: string,
  slot: any
): Promise<CancelSlotResponse> {
  let baseurl = "";
  if (hostname.includes("localhost")) {
    baseurl = "http://localhost:8080";
  } else if (hostname.includes("dev")) {
    baseurl = "https://candidateinterviewscheduler-dev.pwc.com";
  } else if (hostname.includes("stg") || hostname.includes("stage")) {
    baseurl = "https://candidateinterviewscheduler-stg.pwc.com";
  } else {
    baseurl = "https://candidateinterviewscheduler.pwc.com";
  }

  const token = await getAccessToken();
  if (!token) {
    throw new Error("Authentication required - please log in");
  }

  // Step 1: Fetch all related slot IDs + interview details for this application
  const relatedRes = await fetch(
    `${baseurl}/api/candidate-dashboard/related-slot-ids/${slotId}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!relatedRes.ok) {
    const errorBody = await relatedRes.text();
    console.error("[CancelInterviewSlotExp] Failed to fetch related slot IDs:", errorBody);
    throw new Error(`Failed to fetch related slot IDs: ${relatedRes.statusText} - ${errorBody}`);
  }

  const relatedData: {
    slot_ids: string[];
    interviews: {
      slot_id: string;
      interviewer_email: string;
      interviewer_name: string;
      start_time: string;
      end_time: string;
      interview_date: string;
      interview_type?: string | null;
    }[];
  } = await relatedRes.json();

  console.log("[CancelInterviewSlotExp] Related slot data:", relatedData);

  // Step 2: Cancel all slots first
  let lastResponse: CancelSlotResponse = { success: false, message: "No slots cancelled" };

  for (const id of relatedData.slot_ids) {
    console.log(candidateEmail, id, "cancelling exp slot");
    const res = await fetch(`${baseurl}/api/candidate-dashboard/cancel-slot-exp`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ slotId: id, cancellation_reason: reason }),
    });

    if (!res.ok) {
      const errorBody = await res.text();
      console.error(`[CancelInterviewSlotExp] Error cancelling slot ${id}:`, errorBody);
      throw new Error(`Cancel failed for slot ${id}: ${res.statusText} - ${errorBody}`);
    }

    lastResponse = await res.json();
  }

  // Step 3: Send ONE email with all interviewers combined
  try {
    const candidateName = slot?.pairing?.candidate_full_name;
    const reqId = slot?.pairing?.requisition_id || slot?.requisition_id || "N/A";
    const jobTitleRaw = slot?.job_title || "N/A";
    const jobTitle = `${reqId} ${jobTitleRaw}`;
    const recruiterEmail = slot?.pairing?.recruiter_email;
    const recruiterName = slot?.pairing?.recruiter_name;

    const interviewerSlots = relatedData.interviews.map((interview) => ({
      interviewer_email: interview.interviewer_email,
      interviewer_name: interview.interviewer_name,
      start_time: interview.start_time,
      end_time: interview.end_time,
      interview_type: interview.interview_type ?? null,
    }));

    const firstInterview = relatedData.interviews[0];

    const payload = {
      email_type: "interview_cancellation",
      additional_data: {
        candidate_email: candidateEmail,
        candidate_name: candidateName,
        job_title: jobTitle,
        interviewer_slots: interviewerSlots,
        interview_date: firstInterview?.interview_date ?? null,
        cancellation_reason: reason,
        send_to_candidate: true,
        send_to_interviewers: true,
        send_to_recruiter: true,
        recruiter_email: recruiterEmail,
        recruiter_name: recruiterName,
        campus_hire: false,
        experienced_hire: true,
      },
    };

    console.log(`[CancelInterviewSlotExp] Sending single combined email:`, JSON.stringify(payload, null, 2));

    const emailRes = await fetch(`${baseurl}/api/communications/emails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!emailRes.ok) {
      const errorBody = await emailRes.text();
      console.error(`[CancelInterviewSlotExp] Email failed:`, errorBody);
    }
  } catch (emailError) {
    console.error(`[CancelInterviewSlotExp] Email error:`, emailError);
  }

  return lastResponse;
}

export async function sendCancelEmail(
  slot: any,
  candidateEmail: string,
  reason: string,
  isExperienced: boolean,
  isCampus: boolean
) {
  let baseurl = "";
  if (hostname.includes("localhost")) {
    baseurl = "http://localhost:8080";
  } else if (hostname.includes("dev")) {
    baseurl = "https://candidateinterviewscheduler-dev.pwc.com";
  } else if (hostname.includes("stg") || hostname.includes("stage")) {
    baseurl = "https://candidateinterviewscheduler-stg.pwc.com";
  } else {
    baseurl = "https://candidateinterviewscheduler.pwc.com"; // fallback production
  }

  const token = await getAccessToken();
  if (!token) {
    throw new Error("Authentication required - please log in");
  }

  const recruiterEmail = slot?.pairing?.recruiter_email;
  const recruiterName = slot?.pairing?.recruiter_name;

  const interviewerSlots = [
    {
      interviewer_email: slot?.pairing?.interviewer_1_id,
      interviewer_name: slot?.pairing?.interviewer_1_name,
      start_time: slot?.pairing?.interview_1_start_time
        ? new Date(slot.pairing.interview_1_start_time).toISOString()
        : null,
      end_time: slot?.pairing?.interview_1_end_time
        ? new Date(slot.pairing.interview_1_end_time).toISOString()
        : null,
      interview_type: slot?.pairing?.interview_1_type ?? null,
    },
    {
      interviewer_email: slot?.pairing?.interviewer_2_id,
      interviewer_name: slot?.pairing?.interviewer_2_name,
      start_time: slot?.pairing?.interview_2_start_time
        ? new Date(slot.pairing.interview_2_start_time).toISOString()
        : null,
      end_time: slot?.pairing?.interview_2_end_time
        ? new Date(slot.pairing.interview_2_end_time).toISOString()
        : null,
      interview_type: slot?.pairing?.interview_2_type ?? null,
    },
    {
      interviewer_email: slot?.pairing?.interviewer_3_id,
      interviewer_name: slot?.pairing?.interviewer_3_name,
      start_time: slot?.pairing?.interview_3_start_time
        ? new Date(slot.pairing.interview_3_start_time).toISOString()
        : null,
      end_time: slot?.pairing?.interview_3_end_time
        ? new Date(slot.pairing.interview_3_end_time).toISOString()
        : null,
      interview_type: slot?.pairing?.interview_3_type ?? null,
    },
  ].filter((s) => s.interviewer_email && s.interviewer_name);

  // Validate required fields before sending
  const candidateName = slot?.pairing?.candidate_full_name;
  // const jobTitle = slot?.job_title;
  const interviewDate = slot?.pairing?.start_time
    ? new Date(slot.pairing.start_time).toISOString().split("T")[0]
    : null;

  const reqId = slot?.pairing?.requisition_id || slot?.requisition_id || 'N/A';
  const jobTitleRaw = slot?.job_title || 'N/A';
  const jobTitle = `${reqId} ${jobTitleRaw}`;

  // Log for debugging
  console.log("[sendCancelEmail] Validation check:", {
    candidateName,
    jobTitle,
    interviewDate,
    interviewerSlotsCount: interviewerSlots.length,
    recruiterEmail,
  });

  // Skip email if required data is missing
  if (!candidateName || !jobTitle || interviewerSlots.length === 0) {
    console.warn("[sendCancelEmail] Skipping email - missing required data:", {
      hasCandidateName: !!candidateName,
      hasJobTitle: !!jobTitle,
      hasInterviewerSlots: interviewerSlots.length > 0,
    });
    return { success: false, message: "Missing required data for cancel email" };
  }

  const payload = {
    email_type: "interview_cancellation",
    additional_data: {
      candidate_email: candidateEmail,
      candidate_name: candidateName,
      job_title: jobTitle,
      interviewer_slots: interviewerSlots,
      interview_date: interviewDate,
      cancellation_reason: reason,
      send_to_candidate: true,
      send_to_interviewers: true,
      send_to_recruiter: true,
      recruiter_email: recruiterEmail,
      recruiter_name: recruiterName,
      campus_hire: isCampus,
      experienced_hire: isExperienced,
    },
  };

  console.log("[sendCancelEmail] Payload:", JSON.stringify(payload, null, 2));

  const res = await fetch(`${baseurl}/api/communications/emails`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorBody = await res.text();
    console.error("[sendCancelEmail] Error response:", errorBody);
    throw new Error(`Email send failed: ${res.statusText} - ${errorBody}`);
  }

  return res.json();
}
